import React from 'react';
import PropTypes from 'prop-types';

import {Hidden, TableCell, TableHead, TableRow, TableSortLabel, Tooltip} from "@mui/material";
import {styled} from "@mui/material/styles";

import {grey25, greyishBlue100, greyishBlue600} from "assets/jss/main";

import ListingCheckbox from "./ListingCheckbox";
import InfoTooltipIcon from "components/CustomTooltip/InfoTooltipIcon";

ListingTableHead.propTypes = {
    rows: PropTypes.array.isRequired,
    sortState: PropTypes.object,
    onRequestSort: PropTypes.func,
    showCheckboxCell: PropTypes.bool,
    showCheckbox: PropTypes.bool,
    // renders the select-all checkbox inside the first column cell instead of
    // a dedicated cell (DS 2.0 lists: the checkbox lives in the image column)
    mergeCheckboxInFirstCell: PropTypes.bool,
    onSelectAllClick: PropTypes.func,
    isSelectAllChecked: PropTypes.bool,
    checkboxDisabled: PropTypes.bool,
};

const StyledTableHead = styled(TableHead)({
    backgroundColor: grey25
});

const HeaderCell = styled(TableCell)({
    height: '48px',
    // Same cascade problem as ListingTableCell: a plain `padding` here loses to MUI's own
    // TableCell styles, so the header would not sit on the same rhythm as the rows.
    '&&': {
        padding: '8px 16px'
    },
    // Explicit, though it is also MUI's default: a bare <th> falls back to the browser's
    // centered alignment, and every column label in the DS is flush left — including the
    // numeric ones, whose cell content is left-aligned too.
    textAlign: 'left',
    // Column widths are negotiated across header AND body, so the head must carry the
    // same shrink-to-content rule as ListingTableCell or it re-inflates the columns.
    width: '1px',
    borderBottom: `1px solid ${greyishBlue100}`,
    color: greyishBlue600,
    fontSize: '14px',
    fontWeight: 400,
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    // 20px on the outer edges, 16px + 16px between columns: the same rhythm as
    // ListingTableCell, so a header sits exactly above the column it labels.
    '&&:first-of-type': {
        paddingLeft: '20px'
    },
    '&&:last-of-type': {
        paddingRight: '20px'
    },
    // Same width rule as the body cell, so the header column the label sits in is the
    // one the browser actually widens.
    '&:last-of-type': {
        width: '100%'
    },
    '& .MuiTableSortLabel-root': {
        color: 'inherit',
        '&:hover, &:focus, &.Mui-active': {
            color: greyishBlue600
        },
        '& .MuiTableSortLabel-icon': {
            color: greyishBlue600
        }
    }
});

const NoSortLabel = styled(TableSortLabel)({
    cursor: 'initial'
});

const MergedCheckboxCellContent = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
});

const HeaderLabel = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px'
});

function ListingTableHead({rows, sortState = {}, onRequestSort, showCheckboxCell = true, showCheckbox = true, mergeCheckboxInFirstCell = false, onSelectAllClick, isSelectAllChecked = false, checkboxDisabled = false}) {
    const createSortHandler = property => event => onRequestSort(event, property);

    const renderMergedCheckboxCell = (row) => (
        <HeaderCell key={row.id}>
            <MergedCheckboxCellContent>
                {showCheckbox && (
                    <ListingCheckbox
                        onChange={onSelectAllClick}
                        disabled={checkboxDisabled}
                        checked={isSelectAllChecked}
                    />
                )}
                <HeaderLabel>
                    <NoSortLabel hideSortIcon>
                        {row.label}
                    </NoSortLabel>
                    {row.tooltipId && <InfoTooltipIcon titleId={row.tooltipId}/>}
                </HeaderLabel>
            </MergedCheckboxCellContent>
        </HeaderCell>
    );

    const renderCell = (row) => (
        <HeaderCell
            key={row.id}
            sortDirection={sortState[`order[${row.id}]`] !== undefined ? sortState[`order[${row.id}]`] : false}
        >
            <HeaderLabel>
                <Tooltip
                    title={row.label}
                    placement={row.numeric ? "bottom-end" : "bottom-start"}
                    disableFocusListener
                    disableHoverListener
                >
                    {row.sort ?
                        <TableSortLabel
                            active={sortState[`order[${row.id}]`] !== undefined}
                            direction={sortState[`order[${row.id}]`]}
                            onClick={createSortHandler(row.id)}
                        >
                            {row.label}
                        </TableSortLabel>
                        :
                        <NoSortLabel hideSortIcon>
                            {row.label}
                        </NoSortLabel>
                    }
                </Tooltip>
                {row.tooltipId && <InfoTooltipIcon titleId={row.tooltipId}/>}
            </HeaderLabel>
        </HeaderCell>
    );

    return (
        <StyledTableHead>
            <TableRow>
                {showCheckboxCell && !mergeCheckboxInFirstCell && (
                    <HeaderCell>
                        {showCheckbox && (
                            <ListingCheckbox
                                onChange={onSelectAllClick}
                                disabled={checkboxDisabled}
                                checked={isSelectAllChecked}
                            />
                        )}
                    </HeaderCell>
                )}
                {rows.map((row, index) => {
                    const cell = mergeCheckboxInFirstCell && 0 === index
                        ? renderMergedCheckboxCell(row)
                        : renderCell(row);

                    if (row.hideOnMobile) {
                        return (
                            <Hidden smDown key={row.id}>
                                {cell}
                            </Hidden>
                        );
                    } else return cell;
                })}
            </TableRow>
        </StyledTableHead>
    );
}

export default ListingTableHead;
